
import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../../api/apiServices";

const NETWORKS = ["MTN", "AIRTEL", "GLO", "9MOBILE"];
const NET_COLOR = { MTN: "#F59E0B", AIRTEL: "#DC2626", GLO: "#16A34A", "9MOBILE": "#0EA5E9" };

// ── Added airtimenigeria ──────────────────────────────────────
const SOURCE_BADGE = {
  vtpass:         { label: "VTP", color: "#059669", bg: "rgba(5,150,105,0.15)"   },
  paygold:        { label: "PG",  color: "#0284C7", bg: "rgba(2,132,199,0.15)"   },
  vtung:          { label: "VTN", color: "#7C3AED", bg: "rgba(124,58,237,0.15)"  },
  airtimenigeria: { label: "AN",  color: "#DC2626", bg: "rgba(220,38,38,0.15)"   },
  custom:         { label: "C",   color: "#9333EA", bg: "rgba(147,51,234,0.15)"  },
};

function fmt(n)  { return Number(n || 0).toLocaleString("en-NG"); }
function fmtDate(d) {
  if (!d) return "Never";
  return new Date(d).toLocaleString("en-NG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

// ── Plan row ─────────────────────────────────────────────────
function PlanRow({ plan, onEdit, onToggle, onReset }) {
  const markup      = plan.sellingPrice - plan.providerPrice;
  const markupPct   = plan.providerPrice > 0 ? ((markup / plan.providerPrice) * 100).toFixed(1) : 0;
  const offerActive = plan.isOffer && (!plan.offerEndsAt || new Date(plan.offerEndsAt) > new Date());
  const srcBadge    = SOURCE_BADGE[plan.source] || SOURCE_BADGE.vtpass;

  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-white/4 last:border-0 hover:bg-white/3 transition-colors group ${!plan.isActive ? "opacity-40" : ""}`}>

      <div className="flex-shrink-0 w-5 flex justify-center">
        {offerActive ? (
          <span className="text-orange-400 text-sm" title="Active offer">🔥</span>
        ) : (
          <div className="w-1.5 h-1.5 rounded-full bg-white/15" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-white text-sm font-medium truncate">{plan.name}</p>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
            style={{ color: srcBadge.color, background: srcBadge.bg }}>
            {srcBadge.label}
          </span>
          {plan.adminHidden === false && !plan.isActive && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 flex-shrink-0">
              Unavailable
            </span>
          )}
          {plan.offerLabel && offerActive && (
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 flex-shrink-0">
              {plan.offerLabel}
            </span>
          )}
          {plan.offerEndsAt && offerActive && (
            <span className="text-[9px] text-white/25 flex-shrink-0">ends {fmtDate(plan.offerEndsAt)}</span>
          )}
          {/* Validity — AirtimeNigeria plans expose this field */}
          {plan.validity && (
            <span className="text-[9px] text-white/20 flex-shrink-0">{plan.validity}</span>
          )}
        </div>
        <p className="text-white/25 text-[10px] font-mono">{plan.variationCode}</p>
      </div>

      <div className="text-right flex-shrink-0 w-20 hidden sm:block">
        <p className="text-white/30 text-xs">₦{fmt(plan.providerPrice)}</p>
        <p className="text-white/15 text-[9px]">provider</p>
      </div>

      <div className="text-right flex-shrink-0 w-24">
        <p className={`text-sm font-bold ${plan.isOverridden ? "text-[#00C896]" : "text-white/70"}`}>
          ₦{fmt(plan.sellingPrice)}
        </p>
        {plan.isOverridden && (
          <p className={`text-[9px] font-semibold ${markup >= 0 ? "text-[#00C896]/60" : "text-red-400/60"}`}>
            {markup >= 0 ? "+" : ""}₦{fmt(Math.abs(markup))} ({markupPct}%)
          </p>
        )}
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <button onClick={() => onEdit(plan)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center" title="Edit">
          <i className="ri-edit-line text-white/50 text-xs" />
        </button>
        {plan.isOverridden && (
          <button onClick={() => onReset(plan._id)} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-amber-500/20 flex items-center justify-center" title="Reset price">
            <i className="ri-refresh-line text-amber-400/60 text-xs" />
          </button>
        )}
        <button onClick={() => onToggle(plan._id, !plan.isActive)}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${plan.isActive ? "bg-white/5 hover:bg-red-500/20" : "bg-[#00C896]/10 hover:bg-[#00C896]/20"}`}
          title={plan.isActive ? "Hide plan" : "Show plan"}>
          <i className={`text-xs ${plan.isActive ? "ri-eye-off-line text-red-400/60" : "ri-eye-line text-[#00C896]/60"}`} />
        </button>
      </div>
    </div>
  );
}

// ── Edit/Create modal ─────────────────────────────────────────
// dataProvider: the provider currently handling data purchases
function EditModal({ plan, allPlans, dataProvider, onClose, onSave }) {
  const isNew = !plan._id;
  const [form, setForm] = useState({
    name:          plan.name          || "",
    description:   plan.description   || "",
    sellingPrice:  plan.sellingPrice  ?? plan.providerPrice ?? "",
    isActive:      plan.isActive      ?? true,
    isOffer:       plan.isOffer       ?? false,
    offerLabel:    plan.offerLabel    || "",
    offerEndsAt:   plan.offerEndsAt   ? new Date(plan.offerEndsAt).toISOString().slice(0, 16) : "",
    sortOrder:     plan.sortOrder     ?? 0,
    adminNote:     plan.adminNote     || "",
    network:       plan.network       || "MTN",
    variationCode: "",
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const syncedPlans = allPlans.filter(p => p.network === form.network && p.source !== "custom");
  const markup = form.sellingPrice && plan.providerPrice ? Number(form.sellingPrice) - plan.providerPrice : 0;

  // Group plans by source so admins can see VTPass vs AirtimeNigeria plans separately
  const plansBySource = syncedPlans.reduce((acc, p) => {
    const src = p.source || "vtpass";
    if (!acc[src]) acc[src] = [];
    acc[src].push(p);
    return acc;
  }, {});

  const handleSave = async () => {
    if (!form.sellingPrice || isNaN(Number(form.sellingPrice))) { setError("Selling price must be a valid number"); return; }
    if (isNew && !form.variationCode) { setError("Please select a base plan"); return; }
    setSaving(true); setError("");
    try { await onSave(form); onClose(); }
    catch (err) { setError(err?.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#161616] rounded-2xl border border-white/10 w-full max-w-md shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <p className="text-white font-bold text-sm">{isNew ? "Create Custom Offer" : `Edit: ${plan.name}`}</p>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs">
              <i className="ri-error-warning-line" /> {error}
            </div>
          )}

          {isNew && (
            <>
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Network</label>
                <div className="grid grid-cols-4 gap-2">
                  {NETWORKS.map(n => (
                    <button key={n} onClick={() => { set("network", n); set("variationCode", ""); }}
                      className={`py-2 rounded-xl text-xs font-bold transition-all ${form.network === n ? "text-black" : "bg-white/5 text-white/40"}`}
                      style={form.network === n ? { background: NET_COLOR[n] } : {}}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">
                  Base Plan
                  {dataProvider && (
                    <span className="ml-1 text-white/20 normal-case">
                      (data provider: {SOURCE_BADGE[dataProvider]?.label || dataProvider})
                    </span>
                  )}
                </label>
                {/* Grouped by source — VTPass plans vs AirtimeNigeria plans */}
                <select value={form.variationCode} onChange={e => set("variationCode", e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C896]/40">
                  <option value="">-- Select base plan --</option>
                  {Object.entries(plansBySource).map(([src, plans]) => (
                    <optgroup key={src} label={`${SOURCE_BADGE[src]?.label || src} plans`}>
                      {plans.map(p => (
                        <option key={p._id} value={p.variationCode}>
                          {p.name} — ₦{fmt(p.providerPrice)}{p.validity ? ` (${p.validity})` : ""}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                  {syncedPlans.length === 0 && <option disabled>No synced plans — sync first</option>}
                </select>
                <p className="text-white/20 text-[10px] mt-1">Purchases route through the selected plan's provider</p>
              </div>
            </>
          )}

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Display Name</label>
            <input value={form.name} onChange={e => set("name", e.target.value)}
              placeholder="e.g. 2GB Weekend Special"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Description (optional)</label>
            <input value={form.description} onChange={e => set("description", e.target.value)}
              placeholder="Short description shown to users"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">
              Selling Price (₦)
              {!isNew && <span className="ml-2 text-white/20 normal-case">Provider: ₦{fmt(plan.providerPrice)}</span>}
            </label>
            <input type="number" value={form.sellingPrice} onChange={e => set("sellingPrice", e.target.value)}
              placeholder="0"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
            {!isNew && form.sellingPrice && (
              <p className={`text-[10px] mt-1 ${markup >= 0 ? "text-[#00C896]/60" : "text-red-400/60"}`}>
                {markup >= 0 ? "Markup" : "Discount"}: {markup >= 0 ? "+" : ""}₦{fmt(Math.abs(markup))}
                {plan.providerPrice > 0 && ` (${((markup / plan.providerPrice) * 100).toFixed(1)}%)`}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium">Mark as Special Offer</p>
              <p className="text-white/30 text-xs">Shows 🔥 badge and appears at top of list</p>
            </div>
            <button onClick={() => set("isOffer", !form.isOffer)}
              className={`w-11 h-6 rounded-full transition-all relative ${form.isOffer ? "bg-[#00C896]" : "bg-white/10"}`}>
              <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${form.isOffer ? "left-6" : "left-1"}`} />
            </button>
          </div>

          {form.isOffer && (
            <div className="space-y-3 bg-white/3 rounded-xl p-3 border border-white/5">
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Offer Label</label>
                <input value={form.offerLabel} onChange={e => set("offerLabel", e.target.value)}
                  placeholder='e.g. "🔥 Best Value"'
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Offer Ends At</label>
                <input type="datetime-local" value={form.offerEndsAt} onChange={e => set("offerEndsAt", e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00C896]/40 [color-scheme:dark]" />
              </div>
            </div>
          )}

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Sort Order (lower = first)</label>
            <input type="number" value={form.sortOrder} onChange={e => set("sortOrder", e.target.value)}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C896]/40" />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium">Visible to Users</p>
              <p className="text-white/30 text-xs">Toggle to show or hide this plan</p>
            </div>
            <button onClick={() => set("isActive", !form.isActive)}
              className={`w-11 h-6 rounded-full transition-all relative ${form.isActive ? "bg-[#00C896]" : "bg-white/10"}`}>
              <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${form.isActive ? "left-6" : "left-1"}`} />
            </button>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Internal Note</label>
            <input value={form.adminNote} onChange={e => set("adminNote", e.target.value)}
              placeholder="e.g. Promo for Ramadan 2025"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] disabled:opacity-50">
            {saving ? "Saving…" : isNew ? "Create Offer" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bulk update modal (unchanged) ─────────────────────────────
function BulkModal({ network, onClose, onApply }) {
  const [action,   setAction]   = useState("markup_pct");
  const [value,    setValue]    = useState("");
  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    setApplying(true);
    try { await onApply(action, value); onClose(); }
    catch {}
    finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#161616] rounded-2xl border border-white/10 w-full max-w-sm shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <p className="text-white font-bold text-sm">Bulk Update — {network}</p>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50 text-xs" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="space-y-2">
            {[
              { val: "markup_pct",  label: "Add % Markup"   },
              { val: "markup_flat", label: "Add Flat ₦"     },
              { val: "reset_all",   label: "Reset all to provider price", noInput: true },
            ].map(opt => (
              <button key={opt.val} onClick={() => setAction(opt.val)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                  action === opt.val ? "border-[#00C896]/40 bg-[#00C896]/10" : "border-white/8 bg-white/4 hover:bg-white/8"
                }`}>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${action === opt.val ? "border-[#00C896]" : "border-white/20"}`}>
                  {action === opt.val && <div className="w-2 h-2 rounded-full bg-[#00C896]" />}
                </div>
                <p className={`text-sm font-medium ${action === opt.val ? "text-[#00C896]" : "text-white/60"}`}>{opt.label}</p>
              </button>
            ))}
          </div>
          {action !== "reset_all" && (
            <input type="number" value={value} onChange={e => setValue(e.target.value)}
              placeholder={action === "markup_pct" ? "Percentage e.g. 5" : "Amount e.g. 100"}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
          )}
          {action === "reset_all" && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 text-amber-400 text-xs">
              ⚠️ Resets all {network} plans to provider price, removing overrides.
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-white/5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold">Cancel</button>
          <button onClick={handleApply} disabled={applying || (action !== "reset_all" && !value)}
            className="flex-1 py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold disabled:opacity-40">
            {applying ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function DataPlansView() {
  const [plans,        setPlans]        = useState([]);
  const [stats,        setStats]        = useState([]);
  // Now tracks the data-specific provider, not the global active provider
  const [dataProvider, setDataProvider] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [activeNetwork, setActiveNetwork] = useState("MTN");
  const [showInactive, setShowInactive] = useState(false);
  const [searchQ,      setSearchQ]      = useState("");
  const [editPlan,     setEditPlan]     = useState(null);
  const [showBulk,     setShowBulk]     = useState(false);
  const [showAllSources, setShowAllSources] = useState(false);
  const [syncing,      setSyncing]      = useState(false);
  const [toast,        setToast]        = useState(null);
  const toastTimer = useRef(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  const fetchAll = useCallback(async () => {
  setLoading(true);
  try {
    const [plansRes, statsRes, providerRes] = await Promise.all([
      apiClient.get("/mtrade/admin/data/plans?showAll=true"),
      apiClient.get("/mtrade/admin/data/stats"),
      apiClient.get("/mtrade/admin/vtu/provider").catch(() => ({ data: null })),
    ]);
    if (plansRes.data.status) setPlans(plansRes.data.data);
    if (statsRes.data.status) setStats(statsRes.data.data);

    if (providerRes.data?.status) {
      const provider = providerRes.data.data.services?.find(s => s.service === "data")?.current ?? null;
      setDataProvider(provider); // → "airtimenigeria"
    }
  } catch {}
  finally { setLoading(false); }
}, []);

  useEffect(() => { fetchAll(); }, []);

  const handleSync = async (network = "all") => {
    setSyncing(true);
    try {
      const res = await apiClient.post(`/mtrade/admin/data/sync?network=${network}`);
      if (res.data.status) {
        const summary = Object.entries(res.data.results)
          .map(([n, r]) => r.ok ? `${n}: +${r.created || 0} new, ${r.updated || 0} updated` : `${n}: failed`)
          .join(" · ");
        showToast(`Sync via ${dataProvider || "provider"} — ${summary}`);
        fetchAll();
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Sync failed", "error");
    } finally { setSyncing(false); }
  };

  const handleToggle = async (id, isActive) => {
    try {
      await apiClient.patch(`/mtrade/admin/data/plans/${id}`, { isActive });
      setPlans(prev => prev.map(p => p._id === id ? { ...p, isActive } : p));
    } catch { showToast("Failed to update", "error"); }
  };

  const handleReset = async (id) => {
    try {
      const res = await apiClient.patch(`/mtrade/admin/data/plans/${id}/reset-price`);
      if (res.data.status) { showToast(res.data.message); setPlans(prev => prev.map(p => p._id === id ? res.data.data : p)); }
    } catch { showToast("Reset failed", "error"); }
  };

  const handleSave = async (form) => {
    if (editPlan._id) {
      const res = await apiClient.patch(`/mtrade/admin/data/plans/${editPlan._id}`, {
        name: form.name, description: form.description,
        sellingPrice: Number(form.sellingPrice), isActive: form.isActive,
        isOffer: form.isOffer, offerLabel: form.offerLabel,
        offerEndsAt: form.offerEndsAt || null, sortOrder: Number(form.sortOrder),
        adminNote: form.adminNote,
      });
      if (res.data.status) {
        setPlans(prev => prev.map(p => p._id === editPlan._id ? res.data.data : p));
        showToast("Plan updated");
      }
    } else {
      const res = await apiClient.post("/mtrade/admin/data/plans", {
        network: form.network, variationCode: form.variationCode,
        name: form.name, description: form.description,
        sellingPrice: Number(form.sellingPrice), isOffer: form.isOffer,
        offerLabel: form.offerLabel, offerEndsAt: form.offerEndsAt || null,
        sortOrder: Number(form.sortOrder),
      });
      if (res.data.status) { setPlans(prev => [...prev, res.data.data]); showToast("Custom offer created"); }
    }
    fetchAll();
  };

  const handleBulk = async (action, value) => {
    const res = await apiClient.patch("/mtrade/admin/data/bulk", { network: activeNetwork, action, value: Number(value) });
    if (res.data.status) { showToast(res.data.message); fetchAll(); }
  };

  const filtered = plans.filter(p => {
    if (p.network !== activeNetwork) return false;
    if (dataProvider && p.source !== dataProvider && p.source !== "custom") return false;
    if (!showInactive && !p.isActive) return false;
    if (!showAllSources && dataProvider && p.source !== dataProvider && p.source !== "custom") return false;
    if (searchQ.trim()) return (
      p.name?.toLowerCase().includes(searchQ.toLowerCase()) ||
      p.variationCode?.toLowerCase().includes(searchQ.toLowerCase())
    );
    return true;
  });

  const networkPlans = plans.filter(p => p.network === activeNetwork);
  const sourceCounts = networkPlans.reduce((acc, p) => { acc[p.source] = (acc[p.source] || 0) + 1; return acc; }, {});
  const unavailCount = networkPlans.filter(p => !p.isActive && !p.adminHidden).length;

  return (
    <div className="space-y-5">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-sm ${
          toast.type === "error" ? "bg-red-500/90 text-white" : "bg-[#00C896]/90 text-black"
        }`}>
          <i className={toast.type === "error" ? "ri-error-warning-line" : "ri-checkbox-circle-line"} />
          {toast.msg}
        </div>
      )}

      {/* Header — shows data-specific provider */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Data Plan Manager</h2>
          <p className="text-white/40 text-xs mt-0.5">
            Manage prices and offers · Data provider:&nbsp;
            {dataProvider ? (
              <span className="font-bold text-[10px] px-2 py-0.5 rounded-full"
                style={{ color: SOURCE_BADGE[dataProvider]?.color || "#00C896", background: SOURCE_BADGE[dataProvider]?.bg || "rgba(0,200,150,0.1)" }}>
                {SOURCE_BADGE[dataProvider]?.label || dataProvider}
              </span>
            ) : <span className="text-white/20">loading…</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setEditPlan({})}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#00C896]/15 border border-[#00C896]/30 rounded-xl text-xs text-[#00C896] font-semibold hover:bg-[#00C896]/25 transition-colors">
            <i className="ri-add-line" /> New Offer
          </button>
          <button onClick={() => setShowBulk(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/8 rounded-xl text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors">
            <i className="ri-stack-line" /> Bulk Update
          </button>
          <button onClick={() => handleSync("all")} disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/8 rounded-xl text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40">
            <i className={`ri-refresh-line ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync All Networks"}
          </button>
          <button onClick={() => setShowAllSources(s => !s)}
          className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold border transition-all ${
            showAllSources ? "bg-white/10 text-white border-white/15" : "bg-[#161616] text-white/40 border-white/8"
            }`}>
              <i className="ri-layers-line" />
              {showAllSources ? "All sources" : `${SOURCE_BADGE[dataProvider]?.label || dataProvider} only`}
          </button>
        </div>
      </div>

      {/* Network tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
        {NETWORKS.map(net => {
          const s      = stats.find(x => x._id === net) || {};
          const active = activeNetwork === net;
          return (
            <button key={net} onClick={() => setActiveNetwork(net)}
              className={`flex-shrink-0 flex flex-col items-start px-4 py-3 rounded-xl border transition-all min-w-[120px] ${
                active ? "border-opacity-40" : "border-white/5 bg-[#161616] hover:bg-white/5"
              }`}
              style={active ? { borderColor: NET_COLOR[net] + "60", background: NET_COLOR[net] + "12" } : {}}>
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-2 h-2 rounded-full" style={{ background: NET_COLOR[net] }} />
                <p className="text-white font-bold text-sm">{net}</p>
              </div>
              <p className="text-white/30 text-[10px]">{s.active || 0} active · {s.offers || 0} offers</p>
              {s.overridden > 0 && <p className="text-[#00C896]/50 text-[9px] mt-0.5">{s.overridden} overridden</p>}
              {/* Source badges — now includes airtimenigeria */}
              <div className="flex gap-1 mt-1 flex-wrap">
                {Object.keys(SOURCE_BADGE)
                  .filter(src => src !== "custom" && s[src] > 0)
                  .map(src => (
                    <span key={src} className="text-[8px] font-bold px-1 rounded"
                      style={{ color: SOURCE_BADGE[src]?.color, background: SOURCE_BADGE[src]?.bg }}>
                      {SOURCE_BADGE[src]?.label} {s[src]}
                    </span>
                  ))}
              </div>
              {s.lastSync && (
                <p className="text-white/15 text-[9px] mt-0.5">
                  {new Date(s.lastSync).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search plans…"
            className="w-full bg-[#161616] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
        </div>
        <button onClick={() => setShowInactive(s => !s)}
          className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold border transition-all ${
            showInactive ? "bg-white/10 text-white border-white/15" : "bg-[#161616] text-white/40 border-white/8 hover:text-white/60"
          }`}>
          <i className={showInactive ? "ri-eye-line" : "ri-eye-off-line"} />
          {showInactive ? "Showing hidden" : "Show hidden"}
          {unavailCount > 0 && !showInactive && (
            <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400">{unavailCount}</span>
          )}
        </button>
        <button onClick={() => handleSync(activeNetwork)} disabled={syncing}
          className="flex items-center gap-1.5 px-3 py-2.5 bg-[#161616] border border-white/8 rounded-xl text-xs text-white/40 hover:text-white/70 transition-colors disabled:opacity-40">
          <i className={`ri-refresh-line ${syncing ? "animate-spin" : ""}`} />
          Sync {activeNetwork}
        </button>
      </div>

      {/* Source legend */}
      {Object.keys(sourceCounts).length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-white/20 text-[10px]">Sources:</span>
          {Object.entries(sourceCounts).map(([src, count]) => (
            <span key={src} className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ color: SOURCE_BADGE[src]?.color, background: SOURCE_BADGE[src]?.bg }}>
              {SOURCE_BADGE[src]?.label || src} · {count}
            </span>
          ))}
        </div>
      )}

      {/* Plan list */}
      <div className="bg-[#161616] rounded-xl border border-white/5 overflow-hidden">
        <div className="hidden sm:grid px-4 py-2.5 border-b border-white/5 text-[9px] uppercase tracking-widest font-bold text-white/20"
          style={{ gridTemplateColumns: "1.5rem 1fr 6rem 7rem 5.5rem" }}>
          <span /><span>Plan</span>
          <span className="text-right">Provider</span>
          <span className="text-right">Selling Price</span>
          <span className="text-center">Actions</span>
        </div>

        {loading ? (
          <div className="divide-y divide-white/4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <div className="w-5 h-5 bg-white/5 rounded animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-36 bg-white/5 rounded animate-pulse" />
                  <div className="h-2 w-24 bg-white/5 rounded animate-pulse" />
                </div>
                <div className="h-3 w-16 bg-white/5 rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14">
            <i className="ri-wifi-line text-white/15 text-4xl mb-2" />
            <p className="text-white/25 text-sm">
              {plans.filter(p => p.network === activeNetwork).length === 0
                ? `No ${activeNetwork} plans — click "Sync ${activeNetwork}" to fetch`
                : "No plans match your search"}
            </p>
          </div>
        ) : (
          <div>
            {filtered.some(p => p.isOffer) && (
              <>
                <div className="px-4 py-2 bg-orange-500/5 border-b border-orange-500/10">
                  <p className="text-orange-400/60 text-[10px] font-bold uppercase tracking-wider">
                    🔥 Special Offers ({filtered.filter(p => p.isOffer).length})
                  </p>
                </div>
                {filtered.filter(p => p.isOffer).map(plan => (
                  <PlanRow key={plan._id} plan={plan} onEdit={setEditPlan} onToggle={handleToggle} onReset={handleReset} />
                ))}
                <div className="px-4 py-2 bg-white/2 border-b border-white/5">
                  <p className="text-white/20 text-[10px] font-bold uppercase tracking-wider">
                    All Plans ({filtered.filter(p => !p.isOffer).length})
                  </p>
                </div>
              </>
            )}
            {filtered.filter(p => !p.isOffer).map(plan => (
              <PlanRow key={plan._id} plan={plan} onEdit={setEditPlan} onToggle={handleToggle} onReset={handleReset} />
            ))}
          </div>
        )}
      </div>

      {editPlan !== null && (
        <EditModal
          plan={editPlan}
          allPlans={plans}
          dataProvider={dataProvider}
          onClose={() => setEditPlan(null)}
          onSave={handleSave}
        />
      )}
      {showBulk && (
        <BulkModal network={activeNetwork} onClose={() => setShowBulk(false)} onApply={handleBulk} />
      )}
    </div>
  );
}
